/**
 * Trend Analytics Component (Phase 3 — Step 3.13).
 *
 * SVG charts for day-of-week patterns, reason distribution, absence clustering.
 */

import React, { useMemo } from 'react';
import { useAppStore } from '@/store';
import { getDayOfWeek } from '@abs/shared-logic';
import { selectLatenessByStartTime } from '@/store/selectors';
import type { AbsenceEntry, Course, DayOfWeekPattern, ReasonDistribution } from '@abs/shared-types';

interface TrendAnalyticsProps {
  course: Course;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Chart dimensions */
const CHART_W = 400;
const CHART_H = 200;
const BAR_W = 40;
const BAR_GAP = 12;
const MARGIN_TOP = 20;
const MARGIN_LEFT = 40;

export function TrendAnalytics({ course }: TrendAnalyticsProps): React.ReactElement {
  const entries = useAppStore((s) => s.entries.filter((e: AbsenceEntry) => e.courseId === course.id));
  const latenessRows = useAppStore((s) => selectLatenessByStartTime(s, course.id));

  const { dayPatterns, reasonDist } = useMemo(() => {
    // Day-of-week pattern
    const dayBuckets = new Map<number, { absences: number; total: number }>();
    for (let d = 0; d <= 6; d++) {
      dayBuckets.set(d, { absences: 0, total: 0 });
    }

    // Count sessions per day of week from meeting patterns
    for (const mp of course.meetingPatterns) {
      const bucket = dayBuckets.get(mp.dayOfWeek);
      if (bucket) bucket.total += 15; // Rough estimate: ~15 weeks per semester
    }
    // If no meeting patterns, use sessions
    if (course.meetingPatterns.length === 0) {
      for (const bucket of dayBuckets.values()) {
        bucket.total = 0;
      }
    }

    // Count absences per day of week
    for (const entry of entries) {
      const dow = getDayOfWeek(entry.date);
      const bucket = dayBuckets.get(dow);
      if (bucket) bucket.absences += 1;
    }

    const dayPatterns: DayOfWeekPattern[] = [];
    for (let d = 0; d <= 6; d++) {
      const bucket = dayBuckets.get(d)!;
      dayPatterns.push({
        dayOfWeek: d,
        dayName: DAY_NAMES[d],
        absenceCount: bucket.absences,
        totalSessions: bucket.total,
        rate: bucket.total > 0 ? bucket.absences / bucket.total : 0,
      });
    }

    // Reason distribution
    const reasonCounts = new Map<string, number>();
    for (const entry of entries) {
      const label = entry.reason ?? 'Unspecified';
      reasonCounts.set(label, (reasonCounts.get(label) ?? 0) + 1);
    }

    const reasonDist: ReasonDistribution[] = [];
    const total = entries.length || 1;
    for (const [reason, count] of reasonCounts) {
      reasonDist.push({
        reason,
        count,
        percentage: Math.round((count / total) * 100),
      });
    }
    reasonDist.sort((a, b) => b.count - a.count);

    return { dayPatterns, reasonDist };
  }, [entries, course]);

  // SVG bar chart for day-of-week
  const maxRate = Math.max(...dayPatterns.map((d) => d.rate), 0.1);
  const chartBars = dayPatterns.map((d, i) => {
    const barH = Math.max(1, (d.rate / maxRate) * (CHART_H - MARGIN_TOP));
    const x = MARGIN_LEFT + i * (BAR_W + BAR_GAP);
    const y = CHART_H - barH;
    return { ...d, x, y, barH };
  });

  // Donut chart for reason distribution
  const totalReasons = reasonDist.reduce((s, r) => s + r.count, 0) || 1;
  const DONUT_R = 60;
  const DONUT_CX = 80;
  const DONUT_CY = 80;
  const CIRCUMFERENCE = 2 * Math.PI * DONUT_R;

  let cumAngle = 0;
  const donutSegments = reasonDist.map((r) => {
    const fraction = r.count / totalReasons;
    const segLen = fraction * CIRCUMFERENCE;
    const start = cumAngle;
    cumAngle += segLen;
    return { ...r, dashArray: `${segLen} ${CIRCUMFERENCE - segLen}`, dashOffset: -start, fraction };
  });

  // Chart colors — matches --color-chart-1 through --color-chart-7 in tokens.css
  const COLORS = ['#4a90d9', '#e8734a', '#50b86c', '#f5a623', '#9b59b6', '#e74c3c', '#1abc9c'];

  return (
    <div className="trend-analytics" role="region" aria-label="Attendance Trend Analytics">
      <h4>Attendance Trends</h4>

      {entries.length === 0 ? (
        <p className="trend-analytics__empty">No absence data to analyze yet.</p>
      ) : (
        <>
          <div className="trend-analytics__chart-group">
            <div className="trend-analytics__chart">
              <h5>Absence Rate by Day of Week</h5>
              <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} width={CHART_W} height={CHART_H} role="img" aria-label="Day of week absence chart">
                {/* Y-axis */}
                <line x1={MARGIN_LEFT} y1={MARGIN_TOP} x2={MARGIN_LEFT} y2={CHART_H} stroke="var(--color-border)" strokeWidth={1} />
                {/* X-axis */}
                <line x1={MARGIN_LEFT} y1={CHART_H} x2={CHART_W} y2={CHART_H} stroke="var(--color-border)" strokeWidth={1} />
                {/* Bars */}
                {chartBars.map((bar) => (
                  <g key={bar.dayOfWeek}>
                    <rect x={bar.x} y={bar.y} width={BAR_W} height={bar.barH} fill="var(--color-primary)" rx={3} opacity={bar.rate > 0 ? 1 : 0.2}>
                      <title>{`${bar.dayName}: ${bar.absenceCount} absences (${(bar.rate * 100).toFixed(0)}%)`}</title>
                    </rect>
                    <text x={bar.x + BAR_W / 2} y={CHART_H + 14} textAnchor="middle" fontSize={10} fill="var(--color-text-secondary)">
                      {bar.dayName.slice(0, 3)}
                    </text>
                  </g>
                ))}
              </svg>
              <p className="stat-caption">Each bar shows the share of that weekday's sessions you missed.</p>
            </div>

            <div className="trend-analytics__chart">
              <h5>Absence Reasons</h5>
              {reasonDist.length > 0 ? (
                <div className="trend-analytics__donut-row">
                  <svg viewBox="0 0 160 160" width={160} height={160} role="img" aria-label="Reason distribution chart">
                    <circle cx={DONUT_CX} cy={DONUT_CY} r={DONUT_R} fill="none" stroke="var(--color-bg-secondary)" strokeWidth={20} />
                    {donutSegments.map((seg, i) => (
                      <circle
                        key={seg.reason}
                        cx={DONUT_CX}
                        cy={DONUT_CY}
                        r={DONUT_R}
                        fill="none"
                        stroke={COLORS[i % COLORS.length]}
                        strokeWidth={20}
                        strokeDasharray={seg.dashArray}
                        strokeDashoffset={seg.dashOffset}
                        transform={`rotate(-90 ${DONUT_CX} ${DONUT_CY})`}
                      >
                        <title>{`${seg.reason}: ${seg.count} (${seg.percentage}%)`}</title>
                      </circle>
                    ))}
                  </svg>
                  <div className="trend-analytics__legend">
                    {reasonDist.map((r, i) => (
                      <div key={r.reason} className="trend-analytics__legend-item">
                        <span className="trend-analytics__legend-swatch" style={{ background: COLORS[i % COLORS.length] }} />
                        <span>{r.reason} ({r.percentage}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p>No reason data.</p>
              )}
              <p className="stat-caption">Share of logged absences attributed to each reason tag.</p>
            </div>
          </div>

          {latenessRows.length > 0 && (
            <div className="trend-analytics__chart">
              <h5>Lateness by Session Start Time</h5>
              <LatenessBars rows={latenessRows} colors={COLORS} />
              <p className="stat-caption">Share of past sessions you arrived late to, bucketed by class start time.</p>
            </div>
          )}

          <div className="trend-analytics__summary">
            <p>Total absences logged: <strong>{entries.length}</strong></p>
          </div>
        </>
      )}
    </div>
  );
}

/** Horizontal bar chart for the lateness-by-start-time profile (TODO-10). */
function LatenessBars({
  rows,
  colors,
}: {
  rows: Array<{ startTime: string; lateCount: number; totalSessions: number; latenessRate: number }>;
  colors: string[];
}): React.ReactElement {
  const BAR_H = 22;
  const BAR_GAP = 8;
  const LABEL_W = 60;
  const CHART_W = 400;
  const maxRate = Math.max(...rows.map((r) => r.latenessRate), 0.1);
  const height = rows.length * (BAR_H + BAR_GAP) + BAR_GAP;
  const trackW = CHART_W - LABEL_W - 20;

  return (
    <svg viewBox={`0 0 ${CHART_W} ${height}`} width={CHART_W} height={height} role="img" aria-label="Lateness by session start time">
      {rows.map((r, i) => {
        const y = BAR_GAP + i * (BAR_H + BAR_GAP);
        const w = Math.max(1, (r.latenessRate / maxRate) * trackW);
        return (
          <g key={r.startTime}>
            <text x={0} y={y + BAR_H / 2 + 4} fontSize={11} fill="var(--color-text-secondary)">{r.startTime}</text>
            <rect x={LABEL_W} y={y} width={trackW} height={BAR_H} fill="var(--color-bg-secondary)" rx={3} />
            <rect x={LABEL_W} y={y} width={w} height={BAR_H} fill={colors[i % colors.length]} rx={3}>
              <title>{`${r.startTime}: ${r.lateCount}/${r.totalSessions} late (${(r.latenessRate * 100).toFixed(0)}%)`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}
